import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {TimeCardModel} from '../models/timecard.model';
import {TimeCardGroup} from '../models/timecard-group.model';
import {ModelListWithCursor} from '../../../commons/angular/models/model-list-with-cursor.model';
import {EntityPaginationModel} from '../../../commons/models/entity-pagination.model';
import {TimeCardHistoryItem} from '../models/timecard-history.model';
import {TimeCardStatusType} from '../../types/timecard-status.type';
import {TimekeepingExportTimecardsPayloadModel} from "../../models/timekeeping-export-timecards-payload.model";
import {TimekeepingExportPayrollPayloadModel} from "../../models/timekeeping-export-payroll-payload.model";
import {ProcessTrackingModel} from '../../../commons/models/process-tracking.model';
import {TimekeepingExportMileagePayloadModel} from "../../models/timekeeping-export-mileage-payload.model";
import {TimekeepingExportMondelezSummaryPayloadModel} from "../../models/timekeeping-export-mondelez-summary-payload.model";
import {TimeCardAnalysis} from '../models/timecard-analysis.model';
import {TimeCardTotalsModel} from '../../models/timecard-totals.model';
import * as _ from 'lodash';

export interface CompleteTimecardsQueryModel {
  ids?: string[];
  excludedIds?: string[];
  accountIds?: string[];
  branchIds?: string[];
  agentIds?: string[];
  types?: string[];
  from?: number;
  to?: number;
  query?: string;
}

export interface UpdateTimeCardsAdminTimeRequest {
  timeCardId: string;
  adminTime: number;
}

export interface GetTimeCardGroupsQueryModel {
  from?: number;
  to?: number;
  branchIds?: string[];
  agentIds?: string[];
  statuses?: string[];
  timeCardAccountIds?: string[];
  timeCardTypes?: string[];
  hasWarning?: boolean;
  hasInfeasibility?: boolean;
  tags?: string[];
  query?: string;
  returnTimeCards?: boolean;
  page?: number;
  size?: number;
  cursor?: string;
  limit?: number;
  sort?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TimekeepingHttpService {

  private readonly baseUrl = `/timecard`;

  constructor(
    @Inject(HttpClient) private http: HttpClient
  ) {
  }

  getTimeCards(queryParams: {
    limit: number;
    cursor?: string;
    agentIds?: string[];
    from?: number;
    to?: number;
    [key: string]: any;
  }): Observable<ModelListWithCursor<TimeCardModel>> {
    let params = new HttpParams();
    Object.keys(queryParams).forEach(key => {
      const value = queryParams[key];
      if (value !== undefined && value !== null) {
        // Handle array parameters (like agentIds)
        if (Array.isArray(value)) {
          value.forEach(item => {
            params = params.append(key, item.toString());
          });
        } else {
          params = params.set(key, value.toString());
        }
      }
    });
    return this.http.get<ModelListWithCursor<TimeCardModel>>(this.baseUrl, {params});
  }

  /**
   * Get timecard groups for agents
   * GET /v2/timecard/group
   * Groups agents with related timecards for a defined range (from/to)
   */
  getTimeCardGroups(queryParams: GetTimeCardGroupsQueryModel): Observable<ModelListWithCursor<TimeCardGroup>> {
    let params = new HttpParams();
    Object.keys(queryParams).forEach(key => {
      const value = queryParams[key];
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(item => {
            params = params.append(key, item.toString());
          });
        } else {
          params = params.set(key, value.toString());
        }
      }
    });
    return this.http.get<ModelListWithCursor<TimeCardGroup>>(`${this.baseUrl}/group`, {params});
  }

  /**
   * Update timecard status
   * POST /v2/timecard/{timeCardId}/status?status={status}
   */
  updateStatus(timeCardId: string, status: TimeCardStatusType): Observable<TimeCardModel> {
    const params = new HttpParams()
      .set('status', status);
    return this.http.post<TimeCardModel>(`${this.baseUrl}/${timeCardId}/status`, null, {params});
  }

  /**
   * Update timecard
   * POST /v2/timecard/{timeCardId}
   * Editable fields: clockInTime, clockOutTime, breaks, reportedData
   */
  updateTimeCard(timeCardId: string, payload: Partial<TimeCardModel>): Observable<TimeCardModel> {
    return this.http.post<TimeCardModel>(`${this.baseUrl}/${timeCardId}`, payload);
  }

  /**
   * Update admin time per time card.
   * PUT /v2/timecard/adminTime
   * Accepts a list of [timeCardId, adminTime] pairs.
   */
  updateTimeCardsAdminTime(payload: UpdateTimeCardsAdminTimeRequest[]): Observable<TimeCardModel[]> {
    return this.http.put<TimeCardModel[]>(`${this.baseUrl}/adminTime`, payload);
  }

  exportUnapprovedTimeCards(payload: TimekeepingExportTimecardsPayloadModel): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/actions/export/unapprovedTimeCards`, payload);
  }

  exportPayroll(payload: TimekeepingExportPayrollPayloadModel): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/actions/export/payroll`, payload);
  }

  exportMileage(payload: TimekeepingExportMileagePayloadModel): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/actions/export/mileage`, payload);
  }

  exportMondelezSummary(payload: TimekeepingExportMondelezSummaryPayloadModel): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/actions/export/timekeeping-summary`, payload);
  }

  /**
   * Complete timecards in bulk
   * POST /v2/timecard/complete
   * Response: ProcessTracking
   */
  completeTimecards(queryParams: CompleteTimecardsQueryModel): Observable<ProcessTrackingModel> {
    let params = new HttpParams();
    Object.keys(queryParams).forEach(key => {
      const value = queryParams[key];
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(item => {
            params = params.append(key, item.toString());
          });
        } else {
          params = params.set(key, value.toString());
        }
      }
    });
    return this.http.post<ProcessTrackingModel>(`${this.baseUrl}/complete`, null, {params});
  }

  /**
   * Get active bulk process (bulk complete, bulk archive or payroll export)
   * GET /v2/timecard/bulk/process?siteId=xxx
   * Response: ProcessTracking (empty if no active process)
   */
  getBulkProcess(): Observable<ProcessTrackingModel | null> {
    return this.http.get<ProcessTrackingModel | null>(`${this.baseUrl}/bulk/process`);
  }

  /**
   * Get timecard history (audit log) — page-based
   * GET /v2/timecard/history/page?timeCardIds={id}&page={page}&size={size}
   */
  getTimeCardHistory(timeCardIds: string | string[], page: number = 0, size: number = 100): Observable<EntityPaginationModel<TimeCardHistoryItem>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    _.castArray(timeCardIds).forEach(id => {
      params = params.append('timeCardIds', id);
    });
    return this.http.get<EntityPaginationModel<TimeCardHistoryItem>>(`${this.baseUrl}/history/page`, {params});
  }

  /**
   * Refresh timecard data from external provider
   * POST /v2/timecard/{timeCardId}/data?provider={provider}&upsert={upsert}
   */
  refreshTimeCardData(timeCardId: string, provider: string, upsert: boolean = true): Observable<TimeCardModel> {
    const params = new HttpParams()
      .set('provider', provider)
      .set('upsert', upsert.toString());
    return this.http.post<TimeCardModel>(`${this.baseUrl}/${timeCardId}/data`, {}, {params});
  }

  /**
   * Get timecard analysis (risks, alerts, validations)
   * GET /v2/timecard/analysis/?ids=id1&ids=id2
   */
  getTimeCardAnalysis(ids: string[]): Observable<TimeCardAnalysis[]> {
    let params = new HttpParams();
    ids.forEach(id => {
      params = params.append('ids', id);
    });
    return this.http.get<TimeCardAnalysis[]>(`${this.baseUrl}/analysis/`, {params});
  }

  getTimeCardTotals(queryParams: GetTimeCardGroupsQueryModel): Observable<TimeCardTotalsModel> {
    return this.http.get<TimeCardTotalsModel>(`${this.baseUrl}/totals`, {
      params: queryParams as {[param: string]: string | number | boolean | readonly (string | number | boolean)[]}
    });
  }
}
